import React, { useState } from "react";
import { styled } from '@mui/material/styles';
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
  getIdTokenResult,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import {
  Email as Mail,
  Lock,
  Visibility as Eye,
  VisibilityOff as EyeOff,
  Security as Shield
} from '@mui/icons-material';

const LoginContainer = styled('div')`
  min-height: 100vh;
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--bg-secondary) 50%, var(--accent-subtle) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-lg);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(ellipse at 20% 50%, rgba(16, 185, 129, 0.1) 0%, transparent 60%),
                radial-gradient(ellipse at 80% 20%, rgba(52, 211, 153, 0.08) 0%, transparent 60%),
                radial-gradient(ellipse at 40% 80%, rgba(16, 185, 129, 0.06) 0%, transparent 60%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 25px 25px, rgba(16, 185, 129, 0.1) 2px, transparent 0);
    background-size: 50px 50px;
    opacity: 0.3;
    pointer-events: none;
  }
`;

const LoginCard = styled('div')`
  background: rgba(45, 52, 45, 0.9);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: var(--radius-xl);
  padding: var(--space-2xl);
  width: 100%;
  max-width: 440px;
  border: 1px solid rgba(16, 185, 129, 0.2);
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(16, 185, 129, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  z-index: 1;
  transition: all var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 35px 70px rgba(0, 0, 0, 0.5),
      0 0 0 1px rgba(16, 185, 129, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }

  @media (max-width: 480px) {
    padding: var(--space-xl);
    margin: var(--space-md);
  }
`;

const LoginHeader = styled('div')`
  text-align: center;
  margin-bottom: var(--space-2xl);
`;

const LogoContainer = styled('div')`
  width: 80px;
  height: 80px;
  background: var(--accent-gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto var(--space-lg);
  box-shadow:
    0 10px 25px rgba(16, 185, 129, 0.3),
    0 0 0 1px rgba(16, 185, 129, 0.2);
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    padding: 2px;
    background: var(--accent-gradient);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask-composite: exclude;
    opacity: 0.6;
  }

  svg {
    width: 32px;
    height: 32px;
    color: white;
  }
`;

const LoginTitle = styled('h1')`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 var(--space-sm);
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const LoginSubtitle = styled('p')`
  color: var(--text-secondary);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 400;
`;

const LoginForm = styled('form')`
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
`;

const ErrorMessage = styled('div')`
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
  padding: var(--space-md) var(--space-lg);
  border-radius: var(--radius-md);
  border: 1px solid rgba(239, 68, 68, 0.2);
  font-size: 0.875rem;
  font-weight: 500;
  backdrop-filter: blur(8px);
`;

const InputGroup = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const InputLabel = styled('label')`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
`;

const InputWrapper = styled('div')`
  position: relative;
`;

const Input = styled('input')`
  width: 100%;
  padding: var(--space-lg) var(--space-lg) var(--space-lg) 3.5rem;
  border: 2px solid ${props => props.focused ? 'var(--accent-primary)' : 'var(--border)'};
  border-radius: var(--radius-md);
  background: rgba(26, 31, 26, 0.6);
  color: var(--text-primary);
  font-family: inherit;
  font-size: 1rem;
  font-weight: 500;
  outline: none;
  transition: all var(--transition-normal);
  backdrop-filter: blur(8px);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent-primary);
    background: rgba(26, 31, 26, 0.8);
    box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
  }

  &:hover:not(:focus) {
    border-color: var(--border-light);
  }
`;

const InputIcon = styled('div')`
  position: absolute;
  left: var(--space-lg);
  top: 50%;
  transform: translateY(-50%);
  color: ${props => props.focused ? 'var(--accent-primary)' : '#c8d8c8'};
  z-index: 10;

  svg {
    width: 20px;
    height: 20px;
    filter: none;
    opacity: 1;
  }
`;

const EyeButton = styled('button')`
  position: absolute;
  right: var(--space-lg);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: var(--space-sm);
  border-radius: var(--radius-sm);
  transition: all var(--transition-fast);

  &:hover {
    color: var(--text-secondary);
    background: rgba(255, 255, 255, 0.05);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const LoginButton = styled('button')`
  width: 100%;
  padding: var(--space-lg);
  background: ${props => props.disabled ? 'var(--surface-hover)' : 'var(--accent-gradient)'};
  border: none;
  border-radius: var(--radius-md);
  color: white;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all var(--transition-normal);
  position: relative;
  overflow: hidden;
  margin-top: var(--space-md);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 15px 35px rgba(16, 185, 129, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }
`;

const LoadingSpinner = styled('div')`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
  margin-right: var(--space-sm);

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoginFooter = styled('div')`
  text-align: center;
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--border-subtle);
`;

const FooterText = styled('p')`
  color: var(--text-tertiary);
  font-size: 0.8rem;
  margin: 0;
`;

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  const isAdminUser = async (user) => {
    const token = await getIdTokenResult(user, true);
    if (token?.claims?.admin === true) return true;

    const snap = await getDoc(doc(db, "users", user.uid));
    return snap.exists() && snap.data()?.role === "admin";
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      const ok = await isAdminUser(cred.user);
      if (!ok) {
        await signOut(auth);
        setError("Access denied. Only admin can login.");
        return;
      }

      window.location.href = "/dashboard";
    } catch (err) {
      setError(err.message || "Login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        <LoginHeader>
          <LogoContainer>
            <Shield />
          </LogoContainer>
          <LoginTitle>Admin Portal</LoginTitle>
          <LoginSubtitle>
            Secure access to SmartGrocer administration
          </LoginSubtitle>
        </LoginHeader>

        <LoginForm onSubmit={handleLogin}>
          {error && <ErrorMessage>{error}</ErrorMessage>}

          <InputGroup>
            <InputLabel>Email Address</InputLabel>
            <InputWrapper>
              <InputIcon focused={emailFocused}>
                <Mail />
              </InputIcon>
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setEmailFocused(true)}
                onBlur={() => setEmailFocused(false)}
                focused={emailFocused}
                required
              />
            </InputWrapper>
          </InputGroup>

          <InputGroup>
            <InputLabel>Password</InputLabel>
            <InputWrapper>
              <InputIcon focused={passwordFocused}>
                <Lock />
              </InputIcon>
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                focused={passwordFocused}
                required
              />
              <EyeButton
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff /> : <Eye />}
              </EyeButton>
            </InputWrapper>
          </InputGroup>

          <LoginButton type="submit" disabled={isLoading}>
            {isLoading && <LoadingSpinner />}
            {isLoading ? "Signing in..." : "Sign In"}
          </LoginButton>
        </LoginForm>

        <LoginFooter>
          <FooterText>
            🔒 Secure admin access • Protected by Firebase Auth
          </FooterText>
        </LoginFooter>
      </LoginCard>
    </LoginContainer>
  );
}

export default Login;
